import { ReactElement } from 'react';
import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { CabSwitch } from '@CabComponents';
import { SchedulingPreferences, SchedulingPreferencesList } from '../../../store/cabinetApi/generated/scheduling';

type NotificationSetting = {
  label: string;
  inAppField: keyof SchedulingPreferences;
  emailField: keyof SchedulingPreferences;
};

const notificationSettings: NotificationSetting[] = [
  { label: 'Meeting booked', inAppField: 'meeting_booked_in_app', emailField: 'meeting_booked_email' },
  {
    label: 'Respondent fills out a poll',
    inAppField: 'poll_response_received_in_app',
    emailField: 'poll_response_received_email',
  },
  {
    label: 'Meeting shared through Cabinet',
    inAppField: 'meeting_shared_in_app',
    emailField: 'meeting_shared_email',
  },
  {
    label: 'Meeting canceled',
    inAppField: 'meeting_cancelled_in_app',
    emailField: 'meeting_cancelled_email',
  },
  {
    label: 'No proposed meeting times work',
    inAppField: 'meeting_no_times_work_in_app',
    emailField: 'meeting_no_times_work_email',
  },
  {
    label: 'Meeting room conflict',
    inAppField: 'meeting_room_conflict_in_app',
    emailField: 'meeting_room_conflict_email',
  },
  {
    label: 'Meeting booking failed',
    inAppField: 'meeting_booking_failed_in_app',
    emailField: 'meeting_booking_failed_email',
  },
  {
    label: 'Meeting attachments failed',
    inAppField: 'meeting_attachment_failed_in_app',
    emailField: 'meeting_attachment_failed_email',
  },
  {
    label: 'Conference link creation failed',
    inAppField: 'conference_creation_failed_in_app',
    emailField: 'conference_creation_failed_email',
  },
  {
    label: 'Invalid Zoom alternate hosts removed',
    inAppField: 'zoom_alternate_hosts_removed_in_app',
    emailField: 'zoom_alternate_hosts_removed_email',
  },
  {
    label: 'Recurring meeting expires soon',
    inAppField: 'recurring_meeting_expiring_in_app',
    emailField: 'recurring_meeting_expiring_email',
  },
  {
    label: 'Meeting needs follow-up',
    inAppField: 'meeting_follow_up_in_app',
    emailField: 'meeting_follow_up_email',
  },
];

export interface SchedulingNotificationsTabProps {
  schedulingPrefs: SchedulingPreferencesList | undefined;
  onSchedulingPrefChange: (pref: Partial<SchedulingPreferences>) => void;
}

export const SchedulingNotificationsTab = ({
  schedulingPrefs,
  onSchedulingPrefChange,
}: SchedulingNotificationsTabProps): ReactElement | null => {
  if (!schedulingPrefs?.user_prefs) return null;

  return (
    <Stack sx={{ gap: 2 }}>
      <Card variant="outlined" sx={{ width: '100%', padding: 1 }}>
        <CardContent>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>Warn me when I enable calendar holds</Typography>
            <CabSwitch
              checked={!schedulingPrefs.user_prefs.silence_executive_hold_notice}
              onChange={(event) => onSchedulingPrefChange({
                id: schedulingPrefs.user_prefs.id,
                silence_executive_hold_notice: !event.target.checked,
              })}
            />
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ width: '100%', padding: 1 }}>
        <CardContent>
          <Typography variant="h6" sx={{ marginBottom: 2 }}>Notifications</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) 64px 64px', sm: 'minmax(0, 1fr) 88px 88px' },
              alignItems: 'center',
              columnGap: 1,
              rowGap: 1,
            }}
          >
            <Box />
            <Typography variant="body2" sx={{ textAlign: 'center' }}>In-app</Typography>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>Email</Typography>

            {notificationSettings.map((setting, index) => (
              <Box key={setting.label} sx={{ display: 'contents' }}>
                {index > 0 && <Divider sx={{ gridColumn: '1 / -1' }} />}
                <Typography>{setting.label}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CabSwitch
                    checked={Boolean(schedulingPrefs.user_prefs[setting.inAppField])}
                    overrides={{ slotProps: { input: { 'aria-label': `${setting.label} in-app notifications` } } }}
                    onChange={(event) => onSchedulingPrefChange({
                      id: schedulingPrefs.user_prefs.id,
                      [setting.inAppField]: event.target.checked,
                    })}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CabSwitch
                    checked={Boolean(schedulingPrefs.user_prefs[setting.emailField])}
                    overrides={{ slotProps: { input: { 'aria-label': `${setting.label} email notifications` } } }}
                    onChange={(event) => onSchedulingPrefChange({
                      id: schedulingPrefs.user_prefs.id,
                      [setting.emailField]: event.target.checked,
                    })}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};
